import type { StateCreator } from 'zustand';
import {
  lpAnalyticsApi,
  type DailyStats,
  type Session,
  type ABVariant,
  type BridgeRow,
  type CreateVariantInput,
} from '../services/lpAnalyticsApi';

export interface LPAnalyticsSlice {
  lpStats: DailyStats[];
  lpStatsTotals: {
    sessions: number;
    conversions: number;
    bounce_rate: number;
    avg_time_on_page: number;
    traffic_sources: Record<string, number>;
    device_split: Record<string, number>;
  } | null;
  lpSessions: Session[];
  lpSessionsTotal: number;
  lpABTests: ABVariant[];
  lpBridge: BridgeRow[];
  lpLoading: boolean;
  lpError: string | null;

  fetchLPStats: (start: string, end: string) => Promise<void>;
  fetchLPSessions: (page: number) => Promise<void>;
  fetchLPABTests: () => Promise<void>;
  fetchLPBridge: (days?: number) => Promise<void>;
  createLPVariant: (data: CreateVariantInput) => Promise<void>;
  updateLPVariant: (id: string, data: { status: 'active' | 'inactive' }) => Promise<void>;
}

export const createLPAnalyticsSlice: StateCreator<LPAnalyticsSlice> = (set) => ({
  lpStats: [],
  lpStatsTotals: null,
  lpSessions: [],
  lpSessionsTotal: 0,
  lpABTests: [],
  lpBridge: [],
  lpLoading: false,
  lpError: null,

  fetchLPStats: async (start, end) => {
    set({ lpLoading: true, lpError: null });
    try {
      const res = await lpAnalyticsApi.getStats(start, end);
      set({ lpStats: res.daily, lpStatsTotals: res.totals, lpLoading: false });
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
    }
  },

  fetchLPSessions: async (page) => {
    set({ lpLoading: true, lpError: null });
    try {
      const res = await lpAnalyticsApi.getSessions(page);
      set({ lpSessions: res.sessions, lpSessionsTotal: res.total, lpLoading: false });
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
    }
  },

  fetchLPABTests: async () => {
    set({ lpLoading: true, lpError: null });
    try {
      const res = await lpAnalyticsApi.getABTests();
      set({ lpABTests: res.variants, lpLoading: false });
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
    }
  },

  fetchLPBridge: async (days = 30) => {
    set({ lpLoading: true, lpError: null });
    try {
      const rows = await lpAnalyticsApi.getBridge(days);
      set({ lpBridge: rows, lpLoading: false });
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
    }
  },

  createLPVariant: async (data) => {
    set({ lpLoading: true, lpError: null });
    try {
      await lpAnalyticsApi.createVariant(data);
      const res = await lpAnalyticsApi.getABTests();
      set({ lpABTests: res.variants, lpLoading: false });
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
    }
  },

  updateLPVariant: async (id, data) => {
    set({ lpLoading: true, lpError: null });
    try {
      await lpAnalyticsApi.updateVariant(id, data);
      const res = await lpAnalyticsApi.getABTests();
      set({ lpABTests: res.variants, lpLoading: false });
    } catch (e) {
      set({ lpLoading: false, lpError: e instanceof Error ? e.message : 'Error' });
    }
  },
});
